import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
// UI
import { CardWrapper } from "../CustomerInformation/CustomerInformation";
import { Text, Grid, Divider, Box } from "@mantine/core";
// utils/helpers
import {
  determineIfWasFreeTrial,
  extractPricesToHumanReadable,
  getRenewalString,
  formatCurrencyStr,
} from "src/services/accountActivation/checkoutDataHelpers";
// types
import type { AccountCreateLambdaResponse } from "src/services/accountActivation/accountActivationTypes";
//
import cx from "./order_summary.module.css";

const BoldText = Text.withProps({ fw: 700 });
const MediumText = Text.withProps({ fw: 600 });
const DividerWithSpacing = Divider.withProps({ mt: "md", mb: "md" });

interface LineItemData {
  title: string;
  subText: string;
  price: string;
  isSubScription: boolean;
}

const Row = React.memo<{
  left: React.ReactNode;
  right: React.ReactNode;
  dividerOnBottom?: boolean;
}>(function Row(props) {
  const { dividerOnBottom = false } = props;

  return (
    <>
      <Grid>
        <Grid.Col span={9}>{props.left}</Grid.Col>
        <Grid.Col span={3} className={cx["text-right"]}>
          {props.right}
        </Grid.Col>
      </Grid>
      {dividerOnBottom ? <DividerWithSpacing /> : null}
    </>
  );
});

interface OrderSummaryProps {
  accountCreateResp: AccountCreateLambdaResponse;
}

export const OrderSummary = React.memo<OrderSummaryProps>((props) => {
  const { t } = useTranslation();

  const { accountCreateResp } = props;

  const renewalString = useMemo(() => {
    return getRenewalString(accountCreateResp, t, true);
  }, [accountCreateResp, t]);

  const renewalStringBottom = useMemo(() => {
    return getRenewalString(accountCreateResp, t, false);
  }, [accountCreateResp, t]);

  const isFreeTrial = useMemo(() => {
    return determineIfWasFreeTrial(accountCreateResp);
  }, [accountCreateResp]);

  const items = useMemo<LineItemData[]>(() => {
    const niceNameIfZero = (cost: string): string => {
      return parseInt(cost) === 0
        ? t("startPage.orderSummaryCard.priceIsZero")
        : formatCurrencyStr(cost);
    };

    const items: LineItemData[] = accountCreateResp.shopifyOrder.line_items.map(
      (li) => {
        return {
          price: li.is_subscription_product
            ? formatCurrencyStr(
                accountCreateResp.order.program_plan_obj.subscription_cost.toString(),
              )
            : niceNameIfZero(li.product_cost),
          title: li.is_subscription_product
            ? li.product_title
            : li.product_name,
          subText: li.is_subscription_product ? renewalString : "",
          isSubScription: li.is_subscription_product,
        };
      },
    );

    // makes sure the sub is always the first time (i.e. before mightier family kit)
    items.sort((a) => (a.isSubScription ? -1 : 1));

    return items;
  }, [accountCreateResp, renewalString, t]);

  const text = useMemo(() => {
    const prices = extractPricesToHumanReadable(accountCreateResp, isFreeTrial);

    const makePlusTaxStr = (cost: string): string => {
      return t("startPage.orderSummaryCard.costPlusTax", { cost });
    };

    return {
      headings: {
        subtotal: t("startPage.orderSummaryCard.subtotal"),
        shipping: t("startPage.orderSummaryCard.shipping"),
        tax: t("startPage.orderSummaryCard.tax"),
        total: t("startPage.orderSummaryCard.totalToday"),
        chargeAfterTrial: t("startPage.orderSummaryCard.chargeAfterTrial"),
      },
      prices: {
        subtotal: prices.subtotal,
        shipping: prices.shipping,
        tax: prices.tax,
        total: prices.total,
        chargeAfterTrial: prices.totalAfterFreeTrial
          ? makePlusTaxStr(prices.totalAfterFreeTrial)
          : "",
      },
    };
  }, [t, accountCreateResp, isFreeTrial]);

  return (
    <CardWrapper
      title={t("startPage.orderSummary")}
      data-testid="order-summary-card"
    >
      <div>
        {items.map((item, idx) => {
          return (
            <Row
              key={idx}
              dividerOnBottom={idx < items.length - 1}
              left={
                <>
                  <BoldText>{item.title}</BoldText>
                  <Text>{item.subText}</Text>
                </>
              }
              right={<Text>{item.price}</Text>}
            />
          );
        })}

        <Row
          left={<MediumText>{text.headings.subtotal}</MediumText>}
          right={
            <MediumText data-testid="prices-subtotal">
              {text.prices.subtotal}
            </MediumText>
          }
        />

        <Row
          left={<MediumText>{text.headings.shipping}</MediumText>}
          right={<MediumText>{text.prices.shipping}</MediumText>}
        />
        <Row
          left={<MediumText>{text.headings.tax}</MediumText>}
          right={<MediumText>{text.prices.tax}</MediumText>}
        />

        <DividerWithSpacing />

        <Row
          left={<MediumText>{text.headings.total}</MediumText>}
          right={<MediumText>{text.prices.total}</MediumText>}
        />

        {isFreeTrial ? (
          <Row
            left={<MediumText>{text.headings.chargeAfterTrial}</MediumText>}
            right={
              <MediumText data-testid="charge-after-free-trial">
                {text.prices.chargeAfterTrial}
              </MediumText>
            }
          />
        ) : null}

        <Box mt="sm" className={cx["text-right"]}>
          <Text>{renewalStringBottom}</Text>
        </Box>
      </div>
    </CardWrapper>
  );
});

OrderSummary.displayName = "OrderSummary";
